import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { getPrenotazioni, updatePrenotazione } from './api/api.private.service';
import { getMediaLink } from './api/utils';

const BookingManagementPage = () => {
  const [bookings, setBookings] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  

  const fetchPrenotazioni = async () => {
    try {
      const data = await getPrenotazioni();
      setBookings(data.data);
    } catch (error) {
      console.error('Errore nel recupero prenotazioni:', error);
    }
  };

  useEffect(() => {
    fetchPrenotazioni();
  }, []);

  const handleConfermaPrenotazione = async (id) => {
    setIsSaving(true);
    try {
      await updatePrenotazione(id, 'CONFIRMED');
      fetchPrenotazioni();
      toast.success(`Prenotazione confermata con successo!`);
    } catch (error) {
      console.error('Errore nella conferma della prenotazione:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRifiutaPrenotazione = async (id) => {
    setIsSaving(true);
    try {
      await updatePrenotazione(id, 'CANCELED');
      fetchPrenotazioni();
      toast.success(`Prenotazione rifiutata con successo!`);
    } catch (error) {
      console.error('Errore nel rifiuto della prenotazione:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT');
  };

  const today = new Date();

  const futureBookings = [...bookings]
    .filter(booking => new Date(booking.data) >= today)
    .sort((a, b) => new Date(a.data) - new Date(b.data));

  const pastBookings = [...bookings]
    .filter(booking => new Date(booking.data) < today)
    .sort((a, b) => new Date(b.data) - new Date(a.data));

  const renderBookingCard = (booking, index) => (
    <div
  key={index}
  className="flex flex-col mt-12 p-6 shadow-md cursor-pointer transition-all duration-300 ease-in-out bg-white"
  onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
>
  {/* ROW 1: Esperimento + Laboratorio */}
  <div className="mb-6 flex flex-col items-center justify-center md:flex-row md:justify-between w-full">
    <div className="text-center md:text-left">
      <small className="text-xs uppercase font-semibold text-gray-400">Esperimento</small>
      <h2 className="text-2xl font-bold text-gray-800">{booking.esperimenti.titolo}</h2>
      <img
        src={getMediaLink(booking.esperimenti.immagine?.url)}
        className="w-32 object-cover mx-auto md:mx-0"
        alt={booking.esperimenti.titolo}
      />
    </div>
    <div className="mt-8 md:mt-0 text-center md:text-left">
      <small className="text-xs uppercase font-semibold text-gray-400">Laboratorio</small>
      <h2 className="text-2xl font-bold text-gray-800">{booking.esperimenti.laboratorio.nome}</h2>
      <img
        src={getMediaLink(booking.esperimenti.laboratorio.immagine?.url)}
        width="300"
        className="mx-auto md:mx-0"
        alt={booking.esperimenti.laboratorio.nome}
      />
    </div>
  </div>

  {/* ROW 2: Data, Slot, Stato */}
  <div className="mb-6 flex flex-col items-center justify-center md:flex-row md:justify-evenly w-full text-center">
    <div className="mb-4 md:mb-0">
      <small className="text-xs uppercase font-semibold text-gray-400">Data prenotazione</small>
      <h3 className="text-xl font-semibold text-gray-800">{formatDate(booking.data)}</h3>
    </div>
    <div className="mb-4 md:mb-0">
      <small className="text-xs uppercase font-semibold text-gray-400">Slot prenotati</small>
      <ul className="text-gray-700">
        {[...booking.slots]
          .sort((a, b) => a.localeCompare(b))
          .map((slot, slotIndex) => (
            <li key={slotIndex} className="text-xl font-semibold text-gray-800">{slot}</li>
          ))}
      </ul>
    </div>
    <div>
      <small className="text-xs uppercase font-semibold text-gray-400">Stato</small>
      {booking.stato === 'CANCELED' && <h3 className="text-md font-semibold text-white bg-red-600 rounded-full pt-1 pb-1 pr-4 pl-4">Annullata</h3>}
      {booking.stato === 'CONFIRMED' && <h3 className="text-md font-semibold text-white bg-green-600 rounded-full pt-1 pb-1 pr-4 pl-4">Confermata</h3>}
      {booking.stato === 'PENDING' && <h3 className="text-md font-semibold text-white bg-blue-600 rounded-full pt-1 pb-1 pr-4 pl-4">In attesa</h3>}
    </div>
  </div>

  {/* Azioni per PENDING */}
  {booking.stato === 'PENDING' && (
    <div className="flex space-x-4 mt-4">
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleConfermaPrenotazione(booking.documentId);
        }}
        className="flex-1 text-center mt-6 block px-4 py-2 shadow-md bg-white text-black border-2 border-black hover:bg-black hover:text-white uppercase text-sm font-semibold"
      >
        Conferma Prenotazione
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          handleRifiutaPrenotazione(booking.documentId);
        }}
        className="flex-1 text-center mt-6 block px-4 py-2 shadow-md bg-white text-black border-2 border-black hover:bg-black hover:text-white uppercase text-sm font-semibold"
      >
        Rifiuta Prenotazione
      </button>
    </div>
  )}

  {/* Azione per CONFIRMED */}
  {booking.stato === 'CONFIRMED' && (
    <div className="flex space-x-4 mt-4">
    <button
      onClick={(e) => {
        e.stopPropagation();
        window.open(booking.link, '_blank');
      }}
      className="flex-1 text-center mt-6 block px-4 py-2 shadow-md bg-white text-black border-2 border-black hover:bg-black hover:text-white uppercase text-sm font-semibold"
    >
      Accedi al laboratorio remoto
    </button>
  </div>
  )}

  {/* Sezione espandibile */}
  <div
    className={`overflow-hidden transition-all duration-500 ease-in-out ${
      expandedIndex === index ? 'max-h-96 opacity-100 mt-4 border-t pt-4' : 'max-h-0 opacity-0'
    }`}
  >
    <p className="text-gray-700"><strong>Username:</strong> {booking.users_permissions_user?.username}</p>
    <p className="text-gray-700"><strong>Email:</strong> {booking.users_permissions_user?.email}</p>
  </div>
</div>
  );

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          'linear-gradient(rgba(255,255,255,.9), rgba(255,255,255,.9)), url("/bg.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <Navbar />
      <div className="py-10 px-6">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-10">Gestione Prenotazioni</h1>

        {/* Prenotazioni future */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-gray-700 border-b pb-2">Prossime prenotazioni</h2>
          {futureBookings.length === 0 ? (
            <p className="text-center text-gray-500 mt-6">Nessuna prenotazione futura trovata.</p>
          ) : (
            futureBookings.map((booking, index) => renderBookingCard(booking, index))
          )}
        </div>

        {/* Prenotazioni passate */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-700 border-b pb-2">Prenotazioni passate</h2>
          {pastBookings.length === 0 ? (
            <p className="text-center text-gray-500 mt-6">Nessuna prenotazione passata trovata.</p>
          ) : (
            pastBookings.map((booking, index) =>
              renderBookingCard(booking, index + futureBookings.length)
            )
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BookingManagementPage;
